import { FocusEvent, useEffect, useRef, useState } from 'react';

import { AnalyticEventAction } from 'analytics';
import { getEventActionOnTextChange } from 'analytics/events/input-usage';
import {
  PathwayAnswerEventAction,
  trackPathwayAnswerAnalyticsEvent
} from 'analytics/events/pathway-answer';

import { castArray, isEmpty, isArray } from 'lodash/fp';

import { useFormContext } from 'react-hook-form';

import { useDeepCompareEffect, useFirstMountState } from 'react-use';

import { useStores } from 'mobx/hooks/useStores';

import PathwaysFetcher from 'fetchers/PathwaysFetcher';

import {
  LightweightPathwayQuestionServerStructure,
  LightweightPathwayServerStructure
} from 'fetchers/responses/pathways.response';

import { TicketSubTypeOption } from 'utils/TicketType.utils';
import { checkForMatchingClinicianAlerts } from 'utils/urgencyAlertUtils';

import {
  defaultSymptomUrgencyValue,
  urgencyToQuestionOutcomeMap,
  questionOutcomeToUrgencyMap,
  SymptomTicketUrgencyValueAndText
} from 'utils/urgencyMappings';

import { TicketLightweightPathwayAnswer } from 'models/OperatorTicket';

import { PathwayOptionOutcome, PathwayQuestionTypes } from 'models/PathwayTemplates';

import { TicketTypeKind } from 'models/TicketTypes';

import { NO_WARNING } from 'views/Patient/PatientMain/PathwayQuestionView';

import {
  lightweightPathwayQuestionsRemoveActions,
  pathwayQuestionTypeToAnalyticsTypeMap
} from 'components/LightweightPathway/LightweightPathway.constants';
import {
  DateQuestionActionMeta,
  DateValue,
  LightweightPathwayDependentFlatQuestion,
  LightweightPathwayDependentQuestionAnswer,
  LightweightPathwayDependentQuestionServerStructure,
  LightweightPathwayMainFlatQuestion,
  LightweightPathwayMainQuestionAnswer,
  LightweightPathwayQuestionAction,
  LightweightPathwayQuestionAnswer,
  LightweightPathwayQuestionOption,
  MultipleQuestionActionMeta,
  MultipleValue,
  QuestionActionMeta,
  SingleQuestionActionMeta,
  SingleValue,
  TextQuestionActionMeta,
  TextValue,
  TextArrayQuestionActionMeta,
  TextArrayValue
} from 'components/LightweightPathway/LightweightPathway.types';
import {
  getLightweightPathwaysFlatQuestions,
  getLightweightQuestionAlert
} from 'components/LightweightPathway/LightweightPathway.utils';
import {
  LightweightPathwaySummary,
  TicketFormField
} from 'components/Ticket/TicketForms/ticket.shared';

interface UseLightweightPathwaysReturnType {
  lightweightPathways: LightweightPathwayServerStructure[];
  handleAnswerChanged: (
    newQuestionAnswer: LightweightPathwayQuestionAnswer,
    actionMeta: QuestionActionMeta
  ) => void;
  isLoading: boolean;
  handleTextQuestionBlur: (
    event: FocusEvent<HTMLInputElement>,
    currentValue: string,
    valueAfterFocus: string,
    question:
      | LightweightPathwayQuestionServerStructure
      | LightweightPathwayDependentQuestionServerStructure
  ) => void;
}

export const useLightweightPathways = (
  patientId: number,
  isFeatureEnable: boolean,
  context: 'create ticket' | 'edit ticket',
  selectors: {
    ticketTypeDropdown: string;
    answers: string;
    urgency: string;
    pathwaySummary: string;
    suggestedUrgency: string;
  },
  prefilledQuestionsAnswers: TicketLightweightPathwayAnswer[] = []
): UseLightweightPathwaysReturnType => {
  const [lightweightPathways, setLightweightPathways] = useState<
    LightweightPathwayServerStructure[]
  >([]);
  const allQuestionsArray = useRef<
    (LightweightPathwayMainFlatQuestion | LightweightPathwayDependentFlatQuestion)[]
  >([]);
  const { ticketTypeDropdown, answers, urgency, pathwaySummary, suggestedUrgency } = selectors;
  const [isLoading, setIsLoading] = useState(false);
  const isPrefilledQuestions = useRef(false);
  const isFirstMount = useFirstMountState();
  const { alertsStore } = useStores();
  const isEditContext = context === 'edit ticket';

  const { watch, setValue, unregister, getValues } = useFormContext();
  const [currentTicketTypeSelectorValue, currentAnswers] = watch([ticketTypeDropdown, answers]);

  const isSymptomsTicket =
    isArray(currentTicketTypeSelectorValue) ||
    currentTicketTypeSelectorValue?.parentKind === TicketTypeKind.symptom;

  /**
   * Handles prefilled questions when editing a ticket
   * @returns Formatted prefilled questions answers
   */
  const handlePrefilledQuestions = () => {
    const formattedPrefilledQuestionsAnswers: LightweightPathwayQuestionAnswer[] = [];

    const getFormattedPrefilledQuestionAnswer = (
      question: LightweightPathwayMainFlatQuestion | LightweightPathwayDependentFlatQuestion
    ) => {
      const matchedQuestionAnswer = prefilledQuestionsAnswers.find(
        (questionAnswer) =>
          questionAnswer.questionId === question.id &&
          // if no pathwayId, it means it's a non-symptom pathway and this check is not needed
          (!questionAnswer.pathwayId || questionAnswer.pathwayId === question.pathway.id)
      );

      if (matchedQuestionAnswer) {
        const prefilledQuestionAnswer: LightweightPathwayQuestionAnswer = {
          ...question,
          value: matchedQuestionAnswer.value
        };

        if (matchedQuestionAnswer.deniedOptions) {
          prefilledQuestionAnswer.deniedOptions = matchedQuestionAnswer.deniedOptions;
        }

        formattedPrefilledQuestionsAnswers.push(prefilledQuestionAnswer);
      }
    };

    allQuestionsArray.current?.forEach((question) => {
      getFormattedPrefilledQuestionAnswer(question);
    });

    setValue(answers, formattedPrefilledQuestionsAnswers);
    setValue(pathwaySummary, getLightweightPathwaySummary(formattedPrefilledQuestionsAnswers));
    isPrefilledQuestions.current = true;

    return formattedPrefilledQuestionsAnswers;
  };

  useEffect(
    function handleTicketTypeChanged() {
      async function handleLightweightPathways() {
        if (!currentTicketTypeSelectorValue) {
          setLightweightPathways([]);
          unregister(answers);
          return;
        }

        try {
          setIsLoading(true);
          const formattedTicketTypeIds = castArray(currentTicketTypeSelectorValue); //when there is 1 symptom the value is an object

          // Extract the IDs from the selected ticket types/symptoms
          const ids = formattedTicketTypeIds.map((item) =>
            Number((item as TicketSubTypeOption).value)
          );

          // Call API with the appropriate parameter type
          const pathwaysResponse = await PathwaysFetcher.getLightweightPathways(
            isSymptomsTicket ? { symptomIds: ids } : { ticketTypeIds: ids }
          );

          // Choose the appropriate pathways based on ticket type
          const safePathways = isSymptomsTicket
            ? pathwaysResponse?.symptomPathways || []
            : pathwaysResponse?.nonSymptomPathways || [];

          setLightweightPathways(safePathways);

          // Get flat questions directly from the new pathway types
          allQuestionsArray.current = getLightweightPathwaysFlatQuestions(safePathways);

          if (!isEmpty(prefilledQuestionsAnswers) && !isPrefilledQuestions.current) {
            // Call the extracted function to handle prefilled questions
            handlePrefilledQuestions();
          } else {
            generateNewLightweightPathwaysQuestionsAnswers();
          }
          handleAlertChanged();
        } finally {
          setIsLoading(false);
        }
      }

      if (isFeatureEnable) {
        handleLightweightPathways();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTicketTypeSelectorValue, unregister, getValues, setValue, answers, isSymptomsTicket]
  );

  useDeepCompareEffect(generateNewLightweightPathwaysQuestionsAnswers, [currentAnswers]);

  const getLightweightPathwaySummary = (
    currentAnswers: LightweightPathwayQuestionAnswer[]
  ): LightweightPathwaySummary[] => {
    // Create a summary for each pathway loaded from server. take into account existing answers
    const summaries: LightweightPathwaySummary[] = [];
    const answeredQuestionIds = new Set(currentAnswers.map((answer) => answer.id));
    const allPathways = new Map();

    // Get all pathway ids, including the ones in the additionalPathways field
    for (const question of allQuestionsArray.current) {
      if (!allPathways.has(question.pathway.id)) {
        allPathways.set(question.pathway.id, question.pathway.title);
      }
    }
    // Create a summary for each pathway id
    for (const pathwayId of allPathways.keys()) {
      const questionsForPathway = [];
      for (const question of allQuestionsArray.current) {
        if (question.pathway.id === pathwayId) {
          questionsForPathway.push(question);
        }
      }

      const unAnsweredQuestionIds = questionsForPathway
        .filter((question) => !answeredQuestionIds.has(question.id))
        .map((question) => question.id);

      summaries.push({
        pathwayId,
        pathwayName: allPathways.get(pathwayId),
        activeQuestionsCount: questionsForPathway.length,
        unansweredQuestions: unAnsweredQuestionIds
      });
    }
    return summaries;
  };

  function generateNewLightweightPathwaysQuestionsAnswers() {
    const newAnswers: (
      | LightweightPathwayMainQuestionAnswer
      | LightweightPathwayDependentQuestionAnswer
    )[] = [];

    const visibleQuestions = getAllVisibleFlatQuestions();

    visibleQuestions.forEach((visibleQuestion) => {
      const foundedAnswer = currentAnswers?.find(
        (questionAnswer: LightweightPathwayQuestionAnswer) =>
          questionAnswer.id === visibleQuestion.id
      );

      if (foundedAnswer) {
        newAnswers.push({
          ...visibleQuestion,
          value: foundedAnswer.value
        });
      }
    });

    setValue(answers, newAnswers);
    setValue(pathwaySummary, getLightweightPathwaySummary(newAnswers));
  }

  function getAllParentQuestions() {
    return allQuestionsArray.current.filter((question) => isEmpty(question.dependsOn));
  }

  function getParentQuestion(parentQuestionId: string, pathwayId: string) {
    const allParentQuestions = getAllParentQuestions();
    return allParentQuestions.find(
      (question) => question.id === parentQuestionId && question.pathway.id === pathwayId
    );
  }

  function getAllVisibleFlatQuestions() {
    const visibleQuestions: (
      | LightweightPathwayMainFlatQuestion
      | LightweightPathwayDependentFlatQuestion
    )[] = [];

    allQuestionsArray.current.forEach((question) => {
      if (isEmpty(question.dependsOn)) {
        visibleQuestions.push(question);
      } else {
        const parentQuestion = getParentQuestion(
          (question as LightweightPathwayDependentFlatQuestion).parentQuestionId!,
          question.pathway.id
        );
        const matchedParentQuestionAnswer = currentAnswers?.find(
          (questionAnswer: LightweightPathwayQuestionAnswer) =>
            questionAnswer.pathway.id === parentQuestion?.pathway?.id &&
            questionAnswer.id === parentQuestion?.id &&
            isEmpty((questionAnswer as LightweightPathwayDependentQuestionAnswer).dependsOn)
        );

        if (matchedParentQuestionAnswer) {
          const matchedParentQuestionAnswerValue = castArray(matchedParentQuestionAnswer.value);
          if (
            question.dependsOn!.some((dependsOnOption) =>
              matchedParentQuestionAnswerValue.some((option) => dependsOnOption === option.id)
            )
          ) {
            visibleQuestions.push(question);
          }
        }
      }
    });

    return visibleQuestions;
  }

  function updatePathwaySummariesOnAnswersChanged(
    newQuestionAnswer: LightweightPathwayQuestionAnswer,
    actionMeta: QuestionActionMeta
  ) {
    const currentSummaries = getValues(pathwaySummary);

    const updateSummaryForPathway = (pathwayId: string) => {
      const relevantSummary = currentSummaries.find(
        (summary: LightweightPathwaySummary) => summary.pathwayId === pathwayId
      );
      switch (actionMeta.action) {
        case LightweightPathwayQuestionAction.AddFirstOption:
        case LightweightPathwayQuestionAction.AddFirst:
          relevantSummary.unansweredQuestions = relevantSummary.unansweredQuestions.filter(
            (questionId: string) => questionId !== newQuestionAnswer.id
          );
          break;
        case LightweightPathwayQuestionAction.RemoveLast:
        case LightweightPathwayQuestionAction.RemoveOption:
        case LightweightPathwayQuestionAction.RemoveLastOption:
          if (actionMeta.action !== LightweightPathwayQuestionAction.RemoveOption) {
            relevantSummary.unansweredQuestions.push(newQuestionAnswer.id);
          }
          const dependentQuestions = (
            allQuestionsArray.current.find(
              (question) => question.id === newQuestionAnswer.id
            ) as LightweightPathwayMainFlatQuestion
          )?.dependentQuestions;

          if (dependentQuestions) {
            dependentQuestions.forEach((dependentQuestion) => {
              const answeredOptions = castArray(
                newQuestionAnswer.value as LightweightPathwayQuestionOption
              ).map((option: LightweightPathwayQuestionOption) => option.id);
              if (
                !dependentQuestion.dependsOn.some((dependsOnOptionId: string) =>
                  answeredOptions.includes(dependsOnOptionId)
                ) &&
                !relevantSummary.unansweredQuestions.includes(dependentQuestion.id)
              ) {
                relevantSummary.unansweredQuestions.push(dependentQuestion.id);
              }
            });
          }
          break;
      }
    };
    const originalQuestion = allQuestionsArray.current.find(
      (question) => question.id === newQuestionAnswer.id
    )!;

    updateSummaryForPathway(originalQuestion.pathway.id);
    setValue(pathwaySummary, currentSummaries);
  }

  const handleLightweightPathwayQuestionAnswerChanged = (
    newQuestionAnswer: LightweightPathwayQuestionAnswer,
    actionMeta: QuestionActionMeta
  ) => {
    updatePathwaySummariesOnAnswersChanged(newQuestionAnswer, actionMeta);

    switch (actionMeta.type) {
      case PathwayQuestionTypes.MULTIPLE:
        handleMultipleQuestionAnswerChanged(
          newQuestionAnswer as LightweightPathwayQuestionAnswer<MultipleValue>,
          actionMeta as MultipleQuestionActionMeta
        );
        handleAlertChanged();
        break;

      case PathwayQuestionTypes.SINGLE:
        handleSingleQuestionAnswerChanged(
          newQuestionAnswer as LightweightPathwayQuestionAnswer<SingleValue>,
          actionMeta as SingleQuestionActionMeta
        );
        handleAlertChanged();
        break;

      case PathwayQuestionTypes.TEXT:
        handleTextQuestionAnswerChanged(
          newQuestionAnswer as LightweightPathwayQuestionAnswer<TextValue>,
          actionMeta as TextQuestionActionMeta
        );
        break;

      case PathwayQuestionTypes.TEXT_ARRAY:
        handleTextArrayQuestionAnswerChanged(
          newQuestionAnswer as LightweightPathwayQuestionAnswer<TextArrayValue>,
          actionMeta as TextArrayQuestionActionMeta
        );
        break;

      case PathwayQuestionTypes.DATE:
        handleDateQuestionAnswerChanged(
          newQuestionAnswer as LightweightPathwayQuestionAnswer<DateValue>,
          actionMeta as DateQuestionActionMeta
        );
        break;
    }

    handleAnalytics(newQuestionAnswer, actionMeta);
  };

  const handleSingleQuestionAnswerChanged = (
    newQuestionAnswer: LightweightPathwayQuestionAnswer<SingleValue>,
    actionMeta: SingleQuestionActionMeta
  ) => {
    const currentLightweightPathwaysAnswers = getValues(answers) || [];

    if (actionMeta.action === LightweightPathwayQuestionAction.RemoveLastOption) {
      //remove the question and all dependent questions
      let removedDependentQuestionIds: string[] = [];
      (
        newQuestionAnswer as LightweightPathwayMainQuestionAnswer<SingleValue>
      ).dependentQuestions?.forEach((question) => removedDependentQuestionIds.push(question.id));

      const newCurrentAnswers = currentLightweightPathwaysAnswers.filter(
        (currentQuestionAnswer: LightweightPathwayQuestionAnswer) => {
          if (
            removedDependentQuestionIds.includes(currentQuestionAnswer.id) &&
            (currentQuestionAnswer as LightweightPathwayDependentQuestionAnswer<MultipleValue>)
              ?.parentQuestionId !== newQuestionAnswer.id
          ) {
            return true;
          }

          return (
            currentQuestionAnswer.id !== newQuestionAnswer.id &&
            !removedDependentQuestionIds.includes(currentQuestionAnswer.id)
          );
        }
      );

      setValue(answers, newCurrentAnswers);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.AddFirstOption) {
      setValue(answers, [...currentLightweightPathwaysAnswers, newQuestionAnswer]);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.ChangeOption) {
      const currentAnswerIndex = currentLightweightPathwaysAnswers.findIndex(
        (answer: LightweightPathwayQuestionAnswer) => answer.id === newQuestionAnswer.id
      );
      setValue(`${answers}.${currentAnswerIndex}`, newQuestionAnswer);
    }
  };

  const handleMultipleQuestionAnswerChanged = (
    newQuestionAnswer: LightweightPathwayQuestionAnswer<MultipleValue>,
    actionMeta: MultipleQuestionActionMeta
  ) => {
    const currentLightweightPathwaysAnswers = getValues(answers) || [];
    if (actionMeta.action === LightweightPathwayQuestionAction.RemoveLastOption) {
      //remove the question and all dependent questions
      let removedDependentQuestionIds: string[] = [];
      (
        newQuestionAnswer as LightweightPathwayMainQuestionAnswer<MultipleValue>
      ).dependentQuestions?.forEach((question) => removedDependentQuestionIds.push(question.id));

      const newCurrentAnswers = currentLightweightPathwaysAnswers.filter(
        (currentQuestionAnswer: LightweightPathwayQuestionAnswer) => {
          if (
            removedDependentQuestionIds.includes(currentQuestionAnswer.id) &&
            (currentQuestionAnswer as LightweightPathwayDependentQuestionAnswer<MultipleValue>)
              ?.parentQuestionId !== newQuestionAnswer.id
          ) {
            return true;
          }

          return (
            currentQuestionAnswer.id !== newQuestionAnswer.id &&
            !removedDependentQuestionIds.includes(currentQuestionAnswer.id)
          );
        }
      );
      setValue(answers, newCurrentAnswers);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.AddFirstOption) {
      setValue(answers, [...currentLightweightPathwaysAnswers, newQuestionAnswer]);
    }

    if (
      actionMeta.action === LightweightPathwayQuestionAction.RemoveOption ||
      actionMeta.action === LightweightPathwayQuestionAction.AddNewOption
    ) {
      const currentAnswerIndex = currentLightweightPathwaysAnswers.findIndex(
        (answer: LightweightPathwayQuestionAnswer) =>
          answer.id === newQuestionAnswer.id && answer.pathway.id === newQuestionAnswer.pathway.id
      );

      setValue(`${answers}.${currentAnswerIndex}`, newQuestionAnswer);
    }
  };

  const handleTextQuestionAnswerChanged = (
    newQuestionAnswer: LightweightPathwayQuestionAnswer<TextValue>,
    actionMeta: TextQuestionActionMeta
  ) => {
    const currentLightweightPathwaysAnswers = getValues(answers) || [];

    if (actionMeta.action === LightweightPathwayQuestionAction.AddFirst) {
      setValue(answers, [...currentLightweightPathwaysAnswers, newQuestionAnswer]);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.Update) {
      const currentAnswerIndex = currentLightweightPathwaysAnswers.findIndex(
        (answer: LightweightPathwayQuestionAnswer) => answer.id === newQuestionAnswer.id
      );
      setValue(`${answers}.${currentAnswerIndex}`, newQuestionAnswer);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.RemoveLast) {
      setValue(
        answers,
        currentLightweightPathwaysAnswers.filter(
          (answer: LightweightPathwayQuestionAnswer) => answer.id !== newQuestionAnswer.id
        )
      );
    }
  };

  const handleTextArrayQuestionAnswerChanged = (
    newQuestionAnswer: LightweightPathwayQuestionAnswer<TextArrayValue>,
    actionMeta: TextArrayQuestionActionMeta
  ) => {
    const currentLightweightPathwaysAnswers = getValues(answers) || [];

    if (actionMeta.action === LightweightPathwayQuestionAction.AddFirst) {
      setValue(answers, [...currentLightweightPathwaysAnswers, newQuestionAnswer]);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.Update) {
      const currentAnswerIndex = currentLightweightPathwaysAnswers.findIndex(
        (answer: LightweightPathwayQuestionAnswer) => answer.id === newQuestionAnswer.id
      );
      setValue(`${answers}.${currentAnswerIndex}`, newQuestionAnswer);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.AddNewOption) {
      const currentAnswerIndex = currentLightweightPathwaysAnswers.findIndex(
        (answer: LightweightPathwayQuestionAnswer) => answer.id === newQuestionAnswer.id
      );
      if (currentAnswerIndex !== -1) {
        setValue(`${answers}.${currentAnswerIndex}`, newQuestionAnswer);
      } else {
        setValue(answers, [...currentLightweightPathwaysAnswers, newQuestionAnswer]);
      }
    }

    if (
      actionMeta.action === LightweightPathwayQuestionAction.RemoveOption ||
      actionMeta.action === LightweightPathwayQuestionAction.RemoveLast
    ) {
      if (newQuestionAnswer.value.length === 0) {
        setValue(
          answers,
          currentLightweightPathwaysAnswers.filter(
            (answer: LightweightPathwayQuestionAnswer) => answer.id !== newQuestionAnswer.id
          )
        );
      } else {
        const currentAnswerIndex = currentLightweightPathwaysAnswers.findIndex(
          (answer: LightweightPathwayQuestionAnswer) => answer.id === newQuestionAnswer.id
        );
        setValue(`${answers}.${currentAnswerIndex}`, newQuestionAnswer);
      }
    }
  };

  const handleDateQuestionAnswerChanged = (
    newQuestionAnswer: LightweightPathwayQuestionAnswer<DateValue>,
    actionMeta: DateQuestionActionMeta
  ) => {
    const currentLightweightPathwaysAnswers = getValues(answers) || [];
    if (actionMeta.action === LightweightPathwayQuestionAction.AddFirst) {
      setValue(answers, [...currentLightweightPathwaysAnswers, newQuestionAnswer]);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.Update) {
      const currentAnswerIndex = currentLightweightPathwaysAnswers.findIndex(
        (answer: LightweightPathwayQuestionAnswer) => answer.id === newQuestionAnswer.id
      );
      setValue(`${answers}.${currentAnswerIndex}`, newQuestionAnswer);
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.RemoveLast) {
      setValue(
        answers,
        currentLightweightPathwaysAnswers.filter(
          (answer: LightweightPathwayQuestionAnswer) => answer.id !== newQuestionAnswer.id
        )
      );
    }
  };

  const handleAlertChanged = () => {
    const currentAnswers = getValues(answers);
    let pathwayUrgency = defaultSymptomUrgencyValue;

    // Calculate urgency from pathway answers
    currentAnswers?.forEach(
      (questionAnswer: LightweightPathwayQuestionAnswer<MultipleValue | SingleValue>) => {
        if (questionAnswer.type === PathwayQuestionTypes.SINGLE) {
          const questionAlert =
            (questionAnswer.value as SingleValue)?.outcome || PathwayOptionOutcome.NONE;

          if (questionAlert > urgencyToQuestionOutcomeMap[pathwayUrgency.value]!) {
            pathwayUrgency = questionOutcomeToUrgencyMap[questionAlert]!;
          }
        }

        if (questionAnswer.type === PathwayQuestionTypes.MULTIPLE) {
          (questionAnswer.value as MultipleValue).forEach((option) => {
            const questionAlert = option.outcome || PathwayOptionOutcome.NONE;

            if (questionAlert > urgencyToQuestionOutcomeMap[pathwayUrgency.value]!) {
              pathwayUrgency = questionOutcomeToUrgencyMap[questionAlert]!;
            }
          });
        }
      }
    );

    // Get urgency from clinician alerts, compare urgencies and use the highest one
    const alertUrgency = getClinicianAlertUrgency();

    let highestUrgency = pathwayUrgency;
    if (alertUrgency) {
      const pathwayUrgencyLevel = pathwayUrgency.value || 0;
      const alertUrgencyLevel = alertUrgency.value || 0;

      if (alertUrgencyLevel > pathwayUrgencyLevel) {
        highestUrgency = alertUrgency;
      }
    }

    // Skip updating the urgency value on first call in edit ticket context
    // This prevents overriding user's previous urgency selection when editing a ticket
    // Also skip if this is not a symptoms ticket - only symptoms tickets should have auto-urgency
    if (!(isFirstMount && isEditContext) && isSymptomsTicket) {
      // Save the urgency - can be changed by the user
      if (highestUrgency.value !== getValues(urgency)?.value) {
        setValue(urgency, highestUrgency);
      }
    }
    // Save the urgency value as the suggested urgency - cannot be changed by the user
    if (isSymptomsTicket) setValue(suggestedUrgency, highestUrgency.value);
  };

  const getClinicianAlertUrgency = (): SymptomTicketUrgencyValueAndText | null => {
    let tickets: TicketFormField[];
    // If we are in edit context, we only have one ticket
    if (isEditContext) {
      tickets = [getValues('ticket')];
    } else {
      tickets = getValues('tickets');
    }

    const { globalAlerts } = alertsStore;

    if (tickets && patientId && globalAlerts) {
      const alertsResult = checkForMatchingClinicianAlerts(tickets, patientId, globalAlerts);
      return alertsResult.highestUrgency;
    }

    return null;
  };

  const handleAnalytics = (
    newQuestionAnswer: LightweightPathwayQuestionAnswer,
    actionMeta: QuestionActionMeta
  ) => {
    if (
      newQuestionAnswer.type === PathwayQuestionTypes.TEXT ||
      newQuestionAnswer.type === PathwayQuestionTypes.TEXT_ARRAY
    ) {
      //we handle "open text" and "text array" differently
      return;
    }

    const alertLevel = getLightweightQuestionAlert(newQuestionAnswer);
    let action: PathwayAnswerEventAction = AnalyticEventAction.Add;

    if (lightweightPathwayQuestionsRemoveActions.includes(actionMeta.action)) {
      action = AnalyticEventAction.Remove;
    }

    if (actionMeta.action === LightweightPathwayQuestionAction.Update) {
      action = AnalyticEventAction.Update;
    }

    trackPathwayAnswerAnalyticsEvent({
      action: action,
      type: pathwayQuestionTypeToAnalyticsTypeMap[newQuestionAnswer.type],
      alert_level: !alertLevel ? NO_WARNING : alertLevel,
      virtual_page: context,
      tab: 'lightweight questions',
      value: newQuestionAnswer.title
    });
  };

  const handleTextQuestionBlur = (
    _: FocusEvent<HTMLInputElement>,
    currentValue: string,
    valueAfterFocus: string,
    question:
      | LightweightPathwayQuestionServerStructure
      | LightweightPathwayDependentQuestionServerStructure
  ) => {
    const action = getEventActionOnTextChange(valueAfterFocus, currentValue);

    trackPathwayAnswerAnalyticsEvent({
      action: action,
      type: 'open text',
      alert_level: NO_WARNING,
      virtual_page: context,
      tab: 'lightweight questions',
      value: question.title
    });
  };

  return {
    lightweightPathways,
    handleAnswerChanged: handleLightweightPathwayQuestionAnswerChanged,
    isLoading,
    handleTextQuestionBlur
  };
};
